import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import {
  InsightsDashboard,
  type InsightsDashboardProps,
} from '@/features/insights/InsightsDashboard'
import { resolveInsight } from '@/features/insights/utils/resolveInsight'
import type { Insight } from '@/features/insights/utils/types'
import { getOrCreateInvestigationsStore } from '@/stores/investigations-store'
import { useCaseContext } from '../../CaseContext'

/** The shape the dashboard hands us on a navigate-to-graph click. */
type NavigateToGraphOptions = Parameters<
  NonNullable<InsightsDashboardProps['onNavigateToGraph']>
>[0]

export default function InvestigateOverviewPage() {
  const { caseId } = useCaseContext()
  const navigate = useNavigate()

  // The dashboard hands us a small `options` shape on click. We translate it
  // into one of our `Insight` kinds, resolve it to a GraphView, push that
  // into the investigations store as a transient view, and navigate to
  // /cases/:id/graph — same handoff the top strip uses, just from a richer
  // surface. Saved investigations stay untouched.
  return (
    <InsightsDashboard
      caseId={caseId}
      onNavigateToGraph={async options => {
        try {
          const insight = optionsToInsight(options)
          if (!insight) {
            toast.info("Couldn't open this in Graph yet")
            return
          }
          const view = await resolveInsight(caseId, insight)
          getOrCreateInvestigationsStore({ caseId })
            .getState()
            .setTransient(view, insight.title, insight.id)
          navigate(`/cases/${caseId}/graph`)
        } catch (err) {
          toast.error(`Failed to open in Graph: ${(err as Error).message}`)
        }
      }}
    />
  )
}

function optionsToInsight(options: NavigateToGraphOptions): Insight | null {
  // Bridge click: two cluster ids + bridge node ids.
  if (
    options.componentId != null &&
    options.componentIdB != null &&
    Array.isArray(options.bridgeNodeIds)
  ) {
    return {
      id: `dashboard-bridge-${options.componentId}-${options.componentIdB}`,
      kind: 'bridge',
      title: options.label ?? `Cluster ${options.componentId} → Cluster ${options.componentIdB}`,
      summary: 'From Insights dashboard',
      metadata: {
        kind: 'bridge',
        componentA: options.componentId,
        componentB: options.componentIdB,
        // The dashboard provides bridge node ids but not full entity metadata.
        // The bridge resolver uses componentA/B (not entity ids) so this is
        // sufficient.
        entityAId: options.bridgeNodeIds[0] ?? '',
        entityAName: '',
        entityAType: 'any',
        entityBId: options.bridgeNodeIds[1] ?? '',
        entityBName: '',
        entityBType: 'any',
      },
      rank: 0,
    }
  }

  // Cluster click: one componentId.
  if (options.componentId != null) {
    return {
      id: `dashboard-cluster-${options.componentId}`,
      kind: 'cluster',
      title: options.label ?? `Cluster ${options.componentId}`,
      summary: 'From Insights dashboard',
      metadata: {
        kind: 'cluster',
        componentId: options.componentId,
        entityCount: 0,
      },
      rank: 0,
    }
  }

  // Top entity click: entityType + entityName + nodeIds.
  if (options.entityType && options.entityName && options.nodeIds?.[0]) {
    return {
      id: `dashboard-entity-${options.nodeIds[0]}`,
      kind: 'top_entity',
      title: options.entityName,
      summary: options.entityType,
      metadata: {
        kind: 'top_entity',
        entityId: options.nodeIds[0],
        entityName: options.entityName,
        entityType: options.entityType,
        score: null,
        degree: null,
        betweenness: null,
        sourceCount: null,
      },
      rank: 0,
    }
  }

  // Generic node click — show its neighbourhood.
  if (options.nodeIds?.length) {
    return {
      id: `dashboard-node-${options.nodeIds[0]}`,
      kind: 'top_entity',
      title: 'Node neighbourhood',
      summary: 'From Insights dashboard',
      metadata: {
        kind: 'top_entity',
        entityId: options.nodeIds[0],
        entityName: '',
        entityType: 'any',
        score: null,
        degree: null,
        betweenness: null,
        sourceCount: null,
      },
      rank: 0,
    }
  }

  return null
}
